import { useId, useState } from 'react';
import type { OutreachOutlierGroupCode, OutreachReincludedCandidate } from '@aegislab/shared';
import { OUTREACH_OUTLIER_OTHER_TEAM_DAYS } from '@aegislab/shared';
import { useOutlierRows } from '@/entities/outreach-candidate';
import { Button, Checkbox } from '@/shared/ui/atoms';
import { rowDetailText } from '../model/outlierGroups';
import * as s from './SendConfirmReinclude.css';

export interface SendConfirmReincludeProps {
  scenarioId: string;
  draftId: string;
  /** 기본으로 빠진 묶음 인원. 아직 못 받았으면 `null`. */
  count: number | null;
  /** 다시 넣을 기본 제외 묶음. */
  groupCode: Extract<OutreachOutlierGroupCode, 'ALREADY_ANSWERED' | 'OTHER_TEAM_RECENT'>;
  /** 미리보기가 돌려준, 이번 발송에 되살려 명단에 든 사람 — 사유와 확인한 마지막 사건 시각. */
  reincluded: readonly OutreachReincludedCandidate[];
  /** 담당자가 고른 사람. 미리보기가 새로 오기 전에도 체크 상태는 이것으로 보인다. */
  selected: ReadonlySet<string>;
  onToggle: (externalCreatorId: string) => void;
}

const n = new Intl.NumberFormat('ko-KR');

/**
 * 「이미 답한 사람 다시 넣기」 — 재포함 명세 §2-1 · IA §3-5 「재포함 내역」 (퀘스트 12bcfe19).
 *
 * 회신·폼 제출로 기본 제외된 사람을 **이번 발송에만** 되살린다. 고른 것은 초안에 저장되지 않고 발송
 * 요청에 실린다 — 같은 주소 쌍을 따로 보내는 결정과 같은 자리(발송 확인)에 둔 이유다. 서버는 담당자가
 * 확인한 마지막 회신·폼 제출 시각을 함께 받아, 그 뒤 새 사건이 있으면 발송을 멈추고 다시 확인하게 한다.
 */
export function SendConfirmReinclude({
  scenarioId,
  draftId,
  count,
  groupCode,
  reincluded,
  selected,
  onToggle,
}: SendConfirmReincludeProps) {
  const [open, setOpen] = useState(false);
  const headingId = useId();
  if ((count === null || count === 0) && selected.size === 0) return null;

  const selectedHere = reincluded.filter((entry) =>
    groupCode === 'OTHER_TEAM_RECENT'
      ? entry.reason === 'OTHER_TEAM_RECENT'
      : entry.reason !== 'OTHER_TEAM_RECENT'
  );
  const heading =
    groupCode === 'OTHER_TEAM_RECENT' ? '다른 팀 연락 후보 다시 넣기' : '이미 답한 사람 다시 넣기';
  const countLabel = groupCode === 'OTHER_TEAM_RECENT' ? '다른 팀 연락' : '이미 답함';
  const description =
    groupCode === 'OTHER_TEAM_RECENT'
      ? `최근 ${OUTREACH_OUTLIER_OTHER_TEAM_DAYS}일 안에 다른 팀이 연락한 사람은 기본으로 빠집니다. 이번 발송에만 다시 넣을 사람을 고릅니다.`
      : '회신하거나 폼을 낸 사람은 기본으로 빠집니다. 다시 보낼 사람만 골라 이번 발송에만 넣습니다.';
  const selectedDescription =
    groupCode === 'OTHER_TEAM_RECENT'
      ? `다시 넣음 ${n.format(selectedHere.length)}명 — 다른 팀 연락. 확인한 뒤 새 연락 기록이 생기면 발송이 멈추고 다시 확인합니다.`
      : answerSelectionDescription(selectedHere);
  return (
    <section className={s.card} aria-labelledby={headingId}>
      <div className={s.head}>
        <h4 id={headingId} className={s.heading}>
          {heading}
        </h4>
        <span className={s.count}>
          {count === null ? '' : `${countLabel} ${n.format(count)}명`}
        </span>
        <Button
          variant="ghost"
          size="xs"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? '명단 닫기' : '명단 보기'}
        </Button>
      </div>
      <p className={s.note}>{selectedHere.length === 0 ? description : selectedDescription}</p>
      {open ? (
        <AnsweredRows
          scenarioId={scenarioId}
          draftId={draftId}
          groupCode={groupCode}
          selected={selected}
          onToggle={onToggle}
        />
      ) : null}
    </section>
  );
}

function answerSelectionDescription(reincluded: readonly OutreachReincludedCandidate[]): string {
  const replied = reincluded.filter((entry) => entry.reason === 'REPLIED').length;
  const formSubmitted = reincluded.filter((entry) => entry.reason === 'FORM_SUBMITTED').length;
  return `다시 넣음 ${n.format(reincluded.length)}명 — 회신 ${n.format(replied)} · 폼 제출 ${n.format(formSubmitted)}. 확인한 뒤 새 회신이나 폼 제출이 오면 발송이 멈추고 다시 확인합니다.`;
}

/** 「이미 답함」 묶음 명단 — 200명씩 넘긴다. 체크는 이번 발송에 다시 넣는다는 뜻이다. */
function AnsweredRows({
  scenarioId,
  draftId,
  groupCode,
  selected,
  onToggle,
}: Pick<
  SendConfirmReincludeProps,
  'scenarioId' | 'draftId' | 'groupCode' | 'selected' | 'onToggle'
>) {
  const [offsets, setOffsets] = useState<number[]>([0]);
  const offset = offsets[offsets.length - 1] ?? 0;
  const page = useOutlierRows(scenarioId, draftId, groupCode, offset);

  if (page.isPending) return <p className={s.note}>명단을 불러오는 중입니다.</p>;
  if (page.isError) return <p className={s.note}>명단을 불러오지 못했습니다.</p>;

  const next = page.data.nextOffset;
  const paged = next !== null || offsets.length > 1;
  return (
    <>
      <p className={s.note}>
        {n.format(page.data.total)}명 중 {n.format(offset + 1)}~
        {n.format(offset + page.data.rows.length)}번째를 보고 있습니다.
      </p>
      <div className={s.scroll}>
        <ul className={s.rows}>
          {page.data.rows.map((row) => {
            const label = row.handle ?? '핸들 모름';
            const checked = selected.has(row.externalCreatorId);
            return (
              <li key={row.externalCreatorId} className={s.row}>
                <span className={s.handle}>{label}</span>
                <span className={s.detail}>{rowDetailText(row.detail)}</span>
                <Checkbox
                  checkboxSize="sm"
                  checked={checked}
                  label="이번 발송에 포함"
                  onChange={() => onToggle(row.externalCreatorId)}
                  aria-label={`${label} 이번 발송에 포함`}
                />
              </li>
            );
          })}
        </ul>
      </div>
      {paged ? (
        <div className={s.actions}>
          <Button
            variant="ghost"
            size="xs"
            disabled={offsets.length === 1}
            onClick={() => setOffsets((list) => list.slice(0, -1))}
          >
            이전
          </Button>
          <Button
            variant="ghost"
            size="xs"
            disabled={next === null}
            onClick={() => next !== null && setOffsets((list) => [...list, next])}
          >
            다음
          </Button>
        </div>
      ) : null}
    </>
  );
}
